import { SetStateAction, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface SearchParamsUpdateOptions {
  replace?: boolean;
}

type SetSearchParam = (
  input: SetStateAction<string>,
  options?: SearchParamsUpdateOptions,
) => void;

export function useSearchParam(
  name: string,
  defaultValue = '',
): [string, SetSearchParam] {
  const navigate = useNavigate();
  const location = useLocation();
  const param = useMemo(
    () => new URLSearchParams(location.search).get(name) ?? defaultValue,
    [name, defaultValue, location.search],
  );
  const setParam = useCallback<SetSearchParam>(
    (input, { replace = false } = {}) => {
      const searchParams = new URLSearchParams(location.search);
      if (typeof input == 'function') {
        input = input(searchParams.get(name) ?? defaultValue);
      }

      if (input) {
        searchParams.set(name, input);
      } else {
        searchParams.delete(name);
      }

      // Some browsers do not support this method yet.
      if (typeof searchParams.sort == 'function') {
        searchParams.sort();
      }

      const nextSearch = searchParams.toString();

      navigate({ search: nextSearch }, { replace });
    },
    [location.search, name, defaultValue, navigate],
  );

  return [param, setParam];
}
